import { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Divider, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import useReportTablePreferences from './useReportTablePreferences';
import exportGridCsv from './exportGridCsv';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Icon-button styling copied from the reporting tables' reference toolbar
// (UserPerformanceTable.jsx ~L620-700) so campaign-manager tables match
// look/behaviour exactly. Intentionally NOT reused by the default variant.
const campaignIconButtonSx = {
    width: 32,
    height: 32,
    border: '1px solid',
    borderColor: '#e2e8f0',
    borderRadius: '8px',
    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
};
const campaignIconSx = { color: '#64748b' };

function CampaignManagerToolbar({
    apiRef,
    onReset,
    handleExport,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    toolbarStart,
    showGrid,
}) {
    return (
        <Box
            data-testid="campaign-manager-toolbar"
            sx={{
                p: 2.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
            }}
        >
            {toolbarStart}
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {onSearchChange && (
                    <TextField
                        size="small"
                        placeholder={searchPlaceholder}
                        value={searchValue ?? ''}
                        onChange={(event) => onSearchChange(event.target.value)}
                        sx={{ width: 220 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                {showGrid && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <Tooltip title="Choose columns" arrow>
                            <IconButton aria-label="Choose columns" size="small" onClick={() => apiRef.current?.showPreferences?.('columns')} sx={campaignIconButtonSx}>
                                <Iconify icon="eva:options-2-outline" width={16} sx={campaignIconSx} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Filter table" arrow>
                            <IconButton aria-label="Filter table" size="small" onClick={() => apiRef.current?.showFilterPanel?.()} sx={campaignIconButtonSx}>
                                <Iconify icon="ic:round-filter-list" width={16} sx={campaignIconSx} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export table" arrow>
                            <IconButton aria-label="Export table" size="small" onClick={handleExport} sx={campaignIconButtonSx}>
                                <Iconify icon="eva:download-outline" width={16} sx={campaignIconSx} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset table" arrow>
                            <IconButton aria-label="Reset table" size="small" onClick={onReset} sx={campaignIconButtonSx}>
                                <Iconify icon="solar:restart-bold" width={16} sx={campaignIconSx} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
        </Box>
    );
}

CampaignManagerToolbar.propTypes = {
    apiRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    onReset: PropTypes.func.isRequired,
    handleExport: PropTypes.func.isRequired,
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func,
    searchPlaceholder: PropTypes.string,
    toolbarStart: PropTypes.node,
    showGrid: PropTypes.bool.isRequired,
};

function ReportTableToolbar({
    apiRef,
    exportFields,
    csvFileName,
    onReset,
    serverMode,
    onExportCsv,
    exportPayload,
    ignoreValueFormatter,
}) {
    const handleExport = () => {
        if (serverMode && onExportCsv) {
            onExportCsv(exportPayload);
            return;
        }
        exportGridCsv(apiRef, {
            fields: exportFields,
            fileName: csvFileName,
            utf8WithBom: true,
        }, ignoreValueFormatter);
    };

    return (
        <GridToolbarContainer
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                p: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <Button type="button" size="small" onClick={handleExport}>
                {serverMode && !onExportCsv ? 'Export current page CSV' : 'Export CSV'}
            </Button>
            <Button type="button" size="small" color="inherit" onClick={onReset}>Reset Table</Button>
        </GridToolbarContainer>
    );
}

ReportTableToolbar.propTypes = {
    apiRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    exportFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    csvFileName: PropTypes.string.isRequired,
    onReset: PropTypes.func.isRequired,
    serverMode: PropTypes.bool.isRequired,
    onExportCsv: PropTypes.func,
    exportPayload: PropTypes.object.isRequired,
    ignoreValueFormatter: PropTypes.bool,
};

export default function ReportDataGrid({
    tableName,
    columns,
    rows = [],
    defaults,
    loading = false,
    error = null,
    csvFileName = null,
    rowCount = undefined,
    paginationMode = 'client',
    sortingMode = 'client',
    filterMode = 'client',
    onPaginationModelChange = null,
    onSortModelChange = null,
    onFilterModelChange = null,
    onExportCsv = null,
    exportContext = {},
    queryResetKey = null,
    onQueryStateChange = null,
    projectSortModel = null,
    projectFilterModel = null,
    getRowId = null,
    renderPreferenceControls = null,
    showGrid = true,
    toolbarVariant = 'default',
    searchValue = undefined,
    onSearchChange = null,
    searchPlaceholder = 'Search...',
    toolbarStart = null,
    hideToolbar = false,
    ...gridProps
}) {
    const apiRef = useGridApiRef();
    const table = useReportTablePreferences(tableName, defaults);
    const {
        hydrated: tableHydrated,
        paginationModel: tablePaginationModel,
        sortModel: tableSortModel,
        filterModel: tableFilterModel,
        onPaginationModelChange: setTablePaginationModel,
    } = table;
    const restoringOrderRef = useRef(false);
    const previousQueryResetKeyRef = useRef(queryResetKey);
    const projectedSortModel = useMemo(() => (
        projectSortModel ? projectSortModel(tableSortModel) : tableSortModel
    ), [projectSortModel, tableSortModel]);
    const projectedFilterModel = useMemo(() => (
        projectFilterModel ? projectFilterModel(tableFilterModel) : tableFilterModel
    ), [projectFilterModel, tableFilterModel]);
    const resolvedGetRowId = useCallback((row) => (
        getRowId ? getRowId(row) : row.id
    ), [getRowId]);

    const orderedColumns = useMemo(() => {
        const byField = new Map(columns.map((column) => [column.field, column]));
        const orderedFields = [...table.columnOrder, ...columns.map((column) => column.field)].filter(
            (field, index, values) => byField.has(field) && values.indexOf(field) === index
        );
        return orderedFields.map((field) => {
            const column = byField.get(field);
            // Columns that declare their own `flex` opt out of the persisted
            // pixel-width override — they size relative to the available
            // container width instead of an independently drag-resizable
            // fixed width (a manual drag-resize is simply not persisted for
            // these). Every existing ReportDataGrid consumer uses fixed
            // widths only, so this is purely additive for flex-based tables
            // (e.g. the Payments tab) needing to fit within a narrower host
            // (a lead-detail tab) rather than a full-width report page.
            if (column.flex != null) return column;
            return {
                ...column,
                ...(Number.isFinite(table.columnWidths[field]) ? { width: table.columnWidths[field] } : {}),
            };
        });
    }, [columns, table.columnOrder, table.columnWidths]);

    useEffect(() => {
        // apiRef.current is null until DataGridPro mounts (and after it unmounts).
        if (!table.hydrated || !apiRef.current) return;
        const currentOrder = apiRef.current.getAllColumns?.().map((column) => column.field) || [];
        const desiredOrder = table.columnOrder.filter((field) => currentOrder.includes(field));
        const comparableCurrent = currentOrder.filter((field) => desiredOrder.includes(field));
        if (
            desiredOrder.length === 0
            || desiredOrder.every((field, index) => comparableCurrent[index] === field)
        ) return;

        restoringOrderRef.current = true;
        const mutableOrder = [...comparableCurrent];
        desiredOrder.forEach((field, targetIndex) => {
            const currentIndex = mutableOrder.indexOf(field);
            if (currentIndex === targetIndex || currentIndex < 0) return;
            apiRef.current?.setColumnIndex(field, targetIndex);
            mutableOrder.splice(currentIndex, 1);
            mutableOrder.splice(targetIndex, 0, field);
        });
        Promise.resolve().then(() => { restoringOrderRef.current = false; });
    }, [apiRef, table.columnOrder, table.hydrated]);

    const handleColumnOrderChange = useCallback((params) => {
        if (restoringOrderRef.current) return;
        const field = params?.column?.field || params?.field;
        const current = [...table.columnOrder];
        const oldIndex = Number.isInteger(params?.oldIndex) ? params.oldIndex : current.indexOf(field);
        const targetIndex = Math.max(0, Math.min(current.length - 1, params?.targetIndex ?? oldIndex));
        if (oldIndex < 0 || !field) return;
        current.splice(oldIndex, 1);
        current.splice(targetIndex, 0, field);
        table.onColumnOrderChange(current);
    }, [table]);

    const handleColumnWidthChange = useCallback((params) => {
        const field = params?.colDef?.field || params?.field;
        if (field && Number.isFinite(params?.width)) {
            table.onColumnWidthsChange({ [field]: params.width });
        }
    }, [table]);

    const handlePaginationChange = useCallback((model) => {
        const pageSizeChanged = model?.pageSize !== table.paginationModel.pageSize;
        const nextModel = pageSizeChanged ? { ...model, page: 0 } : model;
        table.onPaginationModelChange(nextModel);
        onPaginationModelChange?.(nextModel);
    }, [onPaginationModelChange, table]);

    const handleSortChange = useCallback((model) => {
        const nextModel = projectSortModel ? projectSortModel(model) : model;
        table.onSortModelChange(nextModel);
        if (table.paginationModel.page !== 0) {
            table.onPaginationModelChange({ ...table.paginationModel, page: 0 });
        }
        onSortModelChange?.(nextModel);
    }, [onSortModelChange, projectSortModel, table]);

    const handleFilterChange = useCallback((model) => {
        const nextModel = projectFilterModel ? projectFilterModel(model) : model;
        table.onFilterModelChange(nextModel);
        if (table.paginationModel.page !== 0) {
            table.onPaginationModelChange({ ...table.paginationModel, page: 0 });
        }
        onFilterModelChange?.(nextModel);
    }, [onFilterModelChange, projectFilterModel, table]);

    useEffect(() => {
        if (!tableHydrated) return;
        if (JSON.stringify(projectedSortModel) !== JSON.stringify(tableSortModel)) {
            table.onSortModelChange(projectedSortModel);
        }
        if (JSON.stringify(projectedFilterModel) !== JSON.stringify(tableFilterModel)) {
            table.onFilterModelChange(projectedFilterModel);
        }
    }, [
        projectedFilterModel,
        projectedSortModel,
        table,
        tableFilterModel,
        tableHydrated,
        tableSortModel,
    ]);

    useEffect(() => {
        if (!tableHydrated || !onQueryStateChange) return;
        let paginationModel = tablePaginationModel;
        if (previousQueryResetKeyRef.current !== queryResetKey) {
            previousQueryResetKeyRef.current = queryResetKey;
            if (paginationModel.page !== 0) {
                paginationModel = { ...paginationModel, page: 0 };
                setTablePaginationModel(paginationModel);
            }
        }
        onQueryStateChange({
            tableName,
            queryResetKey,
            paginationModel,
            sortModel: projectedSortModel,
            filterModel: projectedFilterModel,
        });
    }, [
        onQueryStateChange,
        projectedFilterModel,
        projectedSortModel,
        queryResetKey,
        setTablePaginationModel,
        tableHydrated,
        tablePaginationModel,
        tableName,
    ]);

    const exportFields = useMemo(() => (
        table.columnOrder.filter((field) => (
            table.columnVisibilityModel[field] !== false
            && columns.some((column) => column.field === field && column.disableExport !== true)
        ))
    ), [columns, table.columnOrder, table.columnVisibilityModel]);
    const serverMode = paginationMode === 'server' || sortingMode === 'server' || filterMode === 'server';
    const exportPayload = useMemo(() => ({
        fields: exportFields,
        sortModel: projectedSortModel,
        filterModel: projectedFilterModel,
        paginationModel: table.paginationModel,
        context: exportContext,
        fileName: csvFileName || tableName,
    }), [
        csvFileName,
        exportContext,
        exportFields,
        table.paginationModel,
        tableName,
        projectedFilterModel,
        projectedSortModel,
    ]);

    const Toolbar = useCallback(() => (
        <ReportTableToolbar
            apiRef={apiRef}
            exportFields={exportFields}
            csvFileName={csvFileName || tableName}
            onReset={table.reset}
            serverMode={serverMode}
            onExportCsv={onExportCsv}
            exportPayload={exportPayload}
            ignoreValueFormatter={typeof gridProps.unstable_ignoreValueFormatterDuringExport === 'object'
                ? gridProps.unstable_ignoreValueFormatterDuringExport?.csvExport
                : gridProps.unstable_ignoreValueFormatterDuringExport}
        />
    ), [
        apiRef,
        csvFileName,
        exportFields,
        exportPayload,
        gridProps.unstable_ignoreValueFormatterDuringExport,
        onExportCsv,
        serverMode,
        table.reset,
        tableName,
    ]);

    const handleCampaignExport = useCallback(() => {
        if (serverMode && onExportCsv) {
            onExportCsv(exportPayload);
            return;
        }
        exportGridCsv(apiRef, {
            fields: exportFields,
            fileName: csvFileName || tableName,
            utf8WithBom: true,
        }, typeof gridProps.unstable_ignoreValueFormatterDuringExport === 'object'
            ? gridProps.unstable_ignoreValueFormatterDuringExport?.csvExport
            : gridProps.unstable_ignoreValueFormatterDuringExport);
    }, [
        apiRef,
        csvFileName,
        exportFields,
        exportPayload,
        gridProps.unstable_ignoreValueFormatterDuringExport,
        onExportCsv,
        serverMode,
        tableName,
    ]);

    const isCampaignManager = toolbarVariant === 'campaign-manager';

    const grid = showGrid && (
        <Box sx={{ width: '100%', minHeight: 360, overflow: 'hidden' }}>
            <DataGridPro
                // jsdom reports every element as zero-width, so the grid
                // virtualises all but the first few columns away and a test
                // can neither read a later cell nor click a row action. This
                // is MUI's own recommendation for testing the grid, and the
                // flag is off in every real build.
                disableVirtualization={process.env.NODE_ENV === 'test'}
                {...gridProps}
                apiRef={apiRef}
                aria-label={`${tableName} report table`}
                rows={Array.isArray(rows) ? rows : []}
                columns={orderedColumns}
                getRowId={resolvedGetRowId}
                loading={loading || !table.hydrated}
                rowCount={rowCount}
                pagination
                paginationMode={paginationMode}
                sortingMode={sortingMode}
                filterMode={filterMode}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                paginationModel={table.paginationModel}
                onPaginationModelChange={handlePaginationChange}
                sortModel={projectedSortModel}
                onSortModelChange={handleSortChange}
                filterModel={projectedFilterModel}
                onFilterModelChange={handleFilterChange}
                columnVisibilityModel={table.columnVisibilityModel}
                onColumnVisibilityModelChange={table.onColumnVisibilityModelChange}
                pinnedColumns={table.pinnedColumns}
                onPinnedColumnsChange={table.onPinnedColumnsChange}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnWidthChange={handleColumnWidthChange}
                multipleColumnsSortingMode="always"
                disableRowSelectionOnClick
                slots={isCampaignManager ? {} : { toolbar: Toolbar }}
                sx={isCampaignManager ? {
                    border: 'none',
                    minWidth: 0,
                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9' },
                    '& .MuiDataGrid-row:hover': { bgcolor: '#f8fafc' },
                    '& .MuiDataGrid-row': { transition: 'background-color 0.15s' },
                } : {
                    border: 0,
                    minWidth: 0,
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.neutral' },
                }}
            />
        </Box>
    );

    if (isCampaignManager) {
        return (
            <Box sx={{ width: '100%', minWidth: 0 }}>
                {hideToolbar ? null : <CampaignManagerToolbar
                    apiRef={apiRef}
                    onReset={table.reset}
                    handleExport={handleCampaignExport}
                    searchValue={searchValue}
                    onSearchChange={onSearchChange}
                    searchPlaceholder={searchPlaceholder}
                    toolbarStart={toolbarStart}
                    showGrid={showGrid}
                />}
                {/* renderPreferenceControls now renders AFTER the toolbar, not
                    above it — hosts that pass it for their hidden-grid
                    card/list view (e.g. SmartSegmentsPage, TemplateGallery)
                    keep their pager; it just no longer floats above the
                    toolbar row inside the card. When the grid IS shown, hosts
                    already null this out themselves (viewMode-style checks),
                    so the DataGrid's own footer pagination stays the only
                    pager — no double-up. */}
                {table.hydrated && renderPreferenceControls ? renderPreferenceControls(table) : null}
                {showGrid && error ? <Alert severity="error" sx={{ m: 2.5, mb: 0 }}>{error}</Alert> : null}
                {grid}
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {table.hydrated && renderPreferenceControls ? renderPreferenceControls(table) : null}
            {showGrid && error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
            {grid}
        </Box>
    );
}

ReportDataGrid.propTypes = {
    tableName: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object),
    defaults: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    csvFileName: PropTypes.string,
    rowCount: PropTypes.number,
    paginationMode: PropTypes.oneOf(['client', 'server']),
    sortingMode: PropTypes.oneOf(['client', 'server']),
    filterMode: PropTypes.oneOf(['client', 'server']),
    onPaginationModelChange: PropTypes.func,
    onSortModelChange: PropTypes.func,
    onFilterModelChange: PropTypes.func,
    onExportCsv: PropTypes.func,
    exportContext: PropTypes.object,
    queryResetKey: PropTypes.string,
    onQueryStateChange: PropTypes.func,
    projectSortModel: PropTypes.func,
    projectFilterModel: PropTypes.func,
    getRowId: PropTypes.func,
    renderPreferenceControls: PropTypes.func,
    showGrid: PropTypes.bool,
    toolbarVariant: PropTypes.oneOf(['default', 'campaign-manager']),
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func,
    searchPlaceholder: PropTypes.string,
    toolbarStart: PropTypes.node,
    // Skip the campaign-manager toolbar entirely (the host renders its own controls).
    hideToolbar: PropTypes.bool,
};
